import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useVerifyEmail } from "@/hooks/api/auth";

type Status = "pending" | "success" | "missing" | "failed";

export default function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const verify = useVerifyEmail();
  const [status, setStatus] = useState<Status>("pending");

  useEffect(() => {
    const token = searchParams.get("token");
    if (!token) {
      setStatus("missing");
      return;
    }
    verify
      .mutateAsync({ token })
      .then(() => setStatus("success"))
      .catch(() => setStatus("failed"));
    // We deliberately run this only when the token in the URL changes —
    // not on every render — so adding `verify` to deps would re-fire it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface-page px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">{t("auth.verify_email_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {status === "pending" && (
            <CardDescription>{t("auth.verify_email_pending")}</CardDescription>
          )}
          {status === "missing" && (
            <div
              role="alert"
              className="rounded-md border border-danger/30 bg-danger-subtle p-3 text-sm text-danger"
            >
              {t("auth.verify_email_missing_token")}
            </div>
          )}
          {status === "failed" && (
            <div
              role="alert"
              className="rounded-md border border-danger/30 bg-danger-subtle p-3 text-sm text-danger"
            >
              {t("auth.verify_email_failed")}
            </div>
          )}
          {status === "success" && (
            <div
              role="status"
              className="rounded-md border border-success/30 bg-success-subtle p-3 text-sm text-success"
            >
              {t("auth.verify_email_success")}
            </div>
          )}

          <div className="mt-6 text-center">
            <Button asChild variant="ghost">
              <Link to="/login">{t("auth.back_to_login")}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
